import { IonButton, IonInput } from "@ionic/react";
import { forwardRef, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getErrorText } from "@/components/atoms/form-ion/utils";
import { useFieldContext } from "@/form/common/hook";
import type { AppFormFieldLabelProps } from "@/form/components/field-label";
import type { AppFormFieldProps } from "@/form/components/types";
import { cn } from "@/lib/style/cva.config";

export type FileFieldProps = AppFormFieldLabelProps & {
  withAsterisk?: boolean;
  withPlaceholder?: boolean;
  accept?: string;
};

export const FileField = forwardRef<
  HTMLIonInputElement,
  React.ComponentProps<typeof IonInput> & AppFormFieldProps & FileFieldProps
>(function FileField({ withAsterisk = true, withPlaceholder = false, accept, ...props }, ref) {
  const { t, i18n } = useTranslation();
  const field = useFieldContext<File | string | null>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    field.handleChange(file);
  };

  const getDisplayValue = () => {
    if (field.state.value instanceof File) {
      return field.state.value.name;
    }
    if (typeof field.state.value === "string" && field.state.value) {
      // Extract filename from URL
      return field.state.value.split("/").pop() || field.state.value;
    }
    return "";
  };

  return (
    <div className="flex flex-col gap-2">
      <IonInput
        ref={ref}
        labelPlacement={props.labelPlacement ?? "stacked"}
        label={t(`fields:${props.name ?? field.name}.label`)}
        placeholder={
          withPlaceholder ? t(`fields:${props.name ?? field.name}.placeholder`) : undefined
        }
        name={field.name}
        value={getDisplayValue()}
        readonly
        onClick={handleFileSelect}
        onIonBlur={field.handleBlur}
        {...props}
        className={cn(
          withAsterisk && "ion-required",
          field.state.meta.isTouched && "ion-touched",
          !!field.state.meta.errors && field.state.meta.errors.length > 0 && "ion-invalid",
          "cursor-pointer",
          props.className,
        )}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />
      {field.state.value && typeof field.state.value === "string" && (
        <IonButton
          size="small"
          fill="outline"
          href={field.state.value}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("common:view-file")}
        </IonButton>
      )}
    </div>
  );
});
